import React from 'react';
import { Globe, Layers, ListFilter, User, Users, UserCheck, Zap, CircleDot } from 'lucide-react';
import { useUserStore } from '../../../lib/stores/userStore';
import { useReferrerStore } from '../../../lib/stores/referrerStore';
import { useCustomStatesStore } from '../../../lib/stores/customStatesStore';
import { useLeadSourceStore } from '../../../lib/stores/leadSourceStore';
import { useLeadPipelineStore } from '../../../lib/stores/leadPipelineStore';
import { COUNTRY_NAMES } from '../../../lib/utils/countries';
import { PropSelectRow, UserSelectRow } from './rows';
import { formatReferrerName } from '../../../lib/utils/referrerDisplayName';

interface CreateClientTopPropsProps {
  formData: Record<string, any>;
  updateFormData: (updates: Record<string, any>) => void;
  validationErrors: Record<string, string>;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/**
 * Righe di proprietà stile Notion in testa al drawer "Nuova anagrafica":
 * icona + label grigia in colonna fissa, valore inline con dropdown al click
 * (stesso pattern PropRow + searchable-select del drawer attività).
 */
export function CreateClientTopProps({ formData, updateFormData, validationErrors }: CreateClientTopPropsProps) {
  const { users } = useUserStore();
  const { referrers } = useReferrerStore();
  const { clientStates, leadStates } = useCustomStatesStore();
  const { leadSources } = useLeadSourceStore();
  const { pipelines, getStagesForPipeline } = useLeadPipelineStore();

  const isLeadState = (() => {
    if (!formData.stato_cliente) return false;
    const selected = [...clientStates, ...leadStates].find((s) => s.id === formData.stato_cliente);
    return selected?.is_lead === true;
  })();

  const stagesForLead =
    !formData.pipeline_id || formData.pipeline_id === 'legacy'
      ? leadStates
      : getStagesForPipeline(formData.pipeline_id);

  const stateOptions = [
    ...clientStates
      .filter((s) => s.is_lead === true)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((state) => ({ value: state.id, label: `${state.name} (Lead)` })),
    ...clientStates
      .filter((s) => s.is_lead === false)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((state) => ({ value: state.id, label: `${state.name} (Cliente)` })),
  ];

  const activeUsers = users.filter((u) => u.active !== false);

  return (
    <div>
      <PropSelectRow
        icon={rowIcon(Globe)}
        label="Paese"
        value={formData.country || ''}
        onChange={(value) => updateFormData({ country: value })}
        options={COUNTRY_NAMES.map((name) => ({ value: name, label: name }))}
        placeholder="Seleziona paese..."
      />
      <PropSelectRow
        icon={rowIcon(CircleDot)}
        label="Stato cliente"
        required
        value={formData.stato_cliente}
        onChange={(value) => updateFormData({ stato_cliente: value })}
        options={stateOptions}
        placeholder="Seleziona stato"
        error={validationErrors.stato_cliente}
      />
      {isLeadState && pipelines.filter((p) => p.is_active && p.id !== 'legacy').length > 0 && (
        <PropSelectRow
          icon={rowIcon(Layers)}
          label="Pipeline"
          value={formData.pipeline_id || ''}
          // Cambiando pipeline la fase precedente non è più valida: si azzera e
          // l'effect del drawer imposta la prima fase della nuova pipeline.
          // Nessuna voce "Senza pipeline": un lead sta sempre in una pipeline (Luca, 17/09/2026).
          onChange={(value) => updateFormData({ pipeline_id: value, pipeline_stage_id: null })}
          options={pipelines
            .filter((p) => p.is_active && p.id !== 'legacy')
            .map((pipeline) => ({ value: pipeline.id, label: pipeline.name }))}
          placeholder="Seleziona pipeline"
        />
      )}
      {isLeadState && (
        <PropSelectRow
          icon={rowIcon(ListFilter)}
          label="Fase pipeline"
          value={
            !formData.pipeline_id || formData.pipeline_id === 'legacy'
              ? formData.lead_stage || ''
              : formData.pipeline_stage_id || ''
          }
          onChange={(value) => {
            if (!formData.pipeline_id || formData.pipeline_id === 'legacy') {
              updateFormData({ lead_stage: value || null, pipeline_stage_id: null });
            } else {
              updateFormData({ pipeline_stage_id: value || null, lead_stage: null });
            }
          }}
          options={stagesForLead
            .filter((stage: any) => stage.is_active)
            .map((stage: any) => ({ value: stage.id, label: stage.name }))}
          placeholder="Seleziona fase"
        />
      )}
      <UserSelectRow
        icon={rowIcon(User)}
        label="Assegnatario"
        value={formData.assigned_to}
        onChange={(value) => updateFormData({ assigned_to: value })}
        users={activeUsers}
      />
      <UserSelectRow
        icon={rowIcon(UserCheck)}
        label="Commerciale"
        value={formData.sales_rep_id}
        onChange={(value) => updateFormData({ sales_rep_id: value })}
        users={activeUsers}
      />
      <PropSelectRow
        icon={rowIcon(Users)}
        label="Segnalatore"
        value={formData.referrer_id}
        onChange={(value) => updateFormData({ referrer_id: value })}
        options={referrers.map((referrer) => ({
          value: referrer.id,
          label: `${formatReferrerName(referrer.full_name)}${referrer.email ? ` (${referrer.email})` : ''}`,
        }))}
        placeholder="Vuoto"
      />
      <PropSelectRow
        icon={rowIcon(Zap)}
        label="Fonte"
        value={formData.lead_source}
        onChange={(value) => updateFormData({ lead_source: value })}
        options={leadSources.map((source) => ({ value: source.id, label: source.name }))}
        placeholder="Vuoto"
      />
    </div>
  );
}
